import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Modal } from '../components/ui/Modal';

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}

/**
 * Promise-based confirmation dialog — `const ok = await confirm({...})`.
 * A drop-in, accessible replacement for window.confirm(), rendered through the
 * Radix-backed Modal (focus-trap, ESC, aria-modal).
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({ title: '' });
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    setOpen(false);
    resolver.current?.(value);
    resolver.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        isOpen={open}
        onClose={() => settle(false)}
        title={opts.title}
        description={opts.description}
        showClose={false}
        className="w-[min(420px,calc(100vw-2rem))]"
      >
        <div className="flex justify-end gap-2 px-5 pb-4 pt-5">
          <button
            type="button"
            onClick={() => settle(false)}
            className="ss-press rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {opts.cancelText ?? 'Cancel'}
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => settle(true)}
            className={`ss-press rounded-lg px-3.5 py-2 text-[13px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              opts.destructive
                ? 'bg-destructive text-white hover:bg-destructive/90'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {opts.confirmText ?? 'Confirm'}
          </button>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}
